import { useCallback, useEffect, useState } from 'react';
import { api, type DemoDescriptor, type DemoSystem } from '../lib/api';
import { DemoDiagram } from '../components/DemoDiagram';
import { navigate } from '../lib/nav';

/**
 * The Demos page — demo-first navigation. Where the Dashboard starts from infrastructure ("here are
 * your systems"), this starts from the story ("here's what we can show") : one card per capability
 * demo with its architecture, what it proves, what it needs, and CTAs that route to a compatible
 * running system's walkthrough — or deep-link into the launch panel with the right pattern selected.
 */

// Per-demo presentation hints the descriptor doesn't carry. Fallbacks keep unknown demos working:
// recommendedPattern falls back to the first catalog pattern via the launch deep-link default.
const DEMO_HINTS: Record<string, { proves: string; recommendedPattern: string }> = {
  bakehouse: {
    proves: 'AIStor is the Iceberg catalog, S3 store, and Delta Sharing server in one box — Databricks reads your tables live, no copy, no S3 keys handed out.',
    recommendedPattern: 'snsd-free-local',
  },
  'aistor-capabilities': {
    proves: 'The core platform tour — tenant isolation, temporary credentials, public buckets, live QoS throttling, tiering, active-active replication, observability.',
    recommendedPattern: 'aistor-capabilities-lab',
  },
};

/** Whether a demo's full story needs the multi-cluster capabilities lab (tiering/replication). */
const needsLab = (d: DemoDescriptor) => !!(d.requires.replication || d.requires.tiering);
const isLab = (s: DemoSystem) => s.pattern === 'aistor-capabilities-lab';

export function Demos() {
  const [demos, setDemos] = useState<DemoDescriptor[] | null>(null);
  const [systems, setSystems] = useState<DemoSystem[]>([]);
  const [dbxConfigured, setDbxConfigured] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [{ demos }, sys, dbx] = await Promise.all([
        api.demos(),
        api.systems().catch(() => ({ systems: [] as DemoSystem[] })),
        api.databricksConfig().catch(() => ({ host: '', configured: false })),
      ]);
      setDemos(demos);
      setSystems(sys.systems);
      setDbxConfigured(dbx.configured);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load demos');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <main className="content">
      <h1>Demos</h1>
      <p className="muted">
        Start from the story: pick what you want to show, then run it on a compatible system — or
        launch one with the right shape in a couple of clicks.
      </p>

      {error && <p className="error">{error}</p>}
      {demos === null && !error && <div className="placeholder loading">Loading demos…</div>}

      <div className="demo-cards">
        {demos?.map((d) => (
          <DemoCard key={d.id} demo={d} systems={systems} dbxConfigured={dbxConfigured} />
        ))}
      </div>
    </main>
  );
}

function DemoCard({
  demo,
  systems,
  dbxConfigured,
}: {
  demo: DemoDescriptor;
  systems: DemoSystem[];
  dbxConfigured: boolean | null;
}) {
  const hints = DEMO_HINTS[demo.id];
  const lab = needsLab(demo);
  const gated = demo.requiresIntegrations.includes('databricks') && dbxConfigured === false;
  const running = systems.filter((s) => s.state === 'running');
  // Best host first: a capabilities lab unlocks every section; anything else runs the core sections.
  const hosts = [...running].sort((a, b) => Number(isLab(b)) - Number(isLab(a)));
  const steps = demo.walkthrough.length;
  const minutes = Math.ceil(steps * 1.3);
  const title = demo.label.replace(/ —.*/, '');
  const subtitle = demo.label.includes('—') ? demo.label.split('—').slice(1).join('—').trim() : null;

  const walkthroughHref = (sysId: string) =>
    `/demo?system=${encodeURIComponent(sysId)}&demo=${encodeURIComponent(demo.id)}`;

  return (
    <section className="demo-card">
      <div className="demo-card-head">
        <div className="demo-card-title">
          <span className="demo-card-name">{title}</span>
          {subtitle && <span className="muted small-note">{subtitle}</span>}
        </div>
        <span className="tile-tag">
          {steps} steps · ~{minutes} min
        </span>
      </div>

      <DemoDiagram demoId={demo.id} activeStepId={null} />

      <p className="demo-card-desc">{hints?.proves ?? demo.description}</p>

      <div className="tile-tags">
        {lab && <span className="tile-tag">full story needs a capabilities lab</span>}
        {demo.requiresIntegrations.includes('databricks') && (
          <span className={`tile-tag ${gated ? 'cap' : ''}`}>
            {gated ? 'needs Databricks — not connected' : 'Databricks connected ✓'}
          </span>
        )}
        {!lab && <span className="tile-tag">runs on any AIStor system</span>}
      </div>

      {gated && (
        <p className="small-note">
          <button type="button" className="link gate-link" onClick={() => navigate('/integrations')}>
            Connect Databricks on the Integrations page →
          </button>
        </p>
      )}

      <div className="actions">
        {hosts.length > 0 ? (
          hosts.slice(0, 3).map((s, i) => (
            <a
              key={s.id}
              className={`btn ${i === 0 ? '' : 'ghost'}`}
              href={walkthroughHref(s.id)}
              target="_blank"
              rel="noopener noreferrer"
              title={
                lab && !isLab(s)
                  ? 'Runs the core sections — tiering & replication need a capabilities lab'
                  : 'Opens the walkthrough in a new tab'
              }
            >
              Run on {s.name}
              {lab && !isLab(s) ? ' (core)' : ''} ↗
            </a>
          ))
        ) : (
          <span className="muted small-note">No running systems yet.</span>
        )}
        <button
          type="button"
          className={`btn ${hosts.length > 0 ? 'ghost' : ''}`}
          onClick={() => navigate(`/?pattern=${encodeURIComponent(hints?.recommendedPattern ?? '')}`)}
        >
          Launch a {lab ? 'capabilities lab' : 'system'} for this →
        </button>
      </div>
    </section>
  );
}
